import React, { useState, useEffect, memo, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Collapse, CircularProgress, Divider } from '@material-ui/core'

import { setConversations } from '../../../redux/conversations/actions'
import { updateMetrics } from '../../../redux/goals/actions'

import messagesApi from '../../../services/messages'
import { isFunction } from '../../../utils/customMethods'

import { makeStyles, useTheme } from '@material-ui/core/styles'

import {
  ExpandLessRounded as UpIcon,
  ExpandMoreRounded as DownIcon,
} from '@material-ui/icons'

import Alert from '../../molecules/feedback/dialogs/alert'
import Demand from '../../organisms/panels/Demand'
import Typography from '../../atoms/display/typography'
import { cockpit, cockpitSkills } from '../../../utils/cockpit'
import { getTab, setTab, tabsError } from '../../../redux/tabs/actions'
import { setActiveConversation } from '../../../redux/activeConversation/actions'

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '0px -20px',
  },
  title: {
    marginBottom: 25,
    padding: '0px 20px',
  },
  menuBar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 1,
    padding: '15px 20px',
    cursor: 'pointer',
    boxShadow: theme.shadows[3],
    backgroundColor: theme.palette.background.paper2,
  },
  loadingSection: {
    display: 'flex',
    justifyContent: 'center',
    padding: 15,
  },
  message: {
    fontSize: '20px',
    textAlign: 'center',
  },
}))

const ChatClose = (props) => {
  const defaultStatus = {
    loading: false,
    success: false,
    error: false,
    message: '',
  }

  const classes = useStyles()
  const theme = useTheme()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const activeConversation = useSelector((state) => state.activeConversation)
  const conversation = useSelector(
    (state) => state.conversations.conversations[activeConversation]
  )
  const messages = useSelector((state) => state.conversations.messages)

  const tabs = useSelector((state) => state.tabs)
  const features = useSelector((state) => state.features)
  const objFeatureTab = useMemo(() => {
    return features?.find((e) => e.name === 'Tab')
  }, [features])

  const [status, setStatus] = useState({
    update: defaultStatus,
  })
  const [tabInfo, setTabInfo] = useState({})
  const [expanded, setExpanded] = useState(true)
  const [isTabulated, setIsTabulated] = useState(false)
  const [updateDemand, setUpdateDemand] = useState(false)
  const [updateDemandLoading, setUpdateDemandLoading] = useState(false)
  const [conversationTA, setConversationTA ] = useState({  startConversation: null, endConversation: null })
  const user = useSelector((state) => state.user)

  useEffect(() => {
    let message = status?.update?.message?.[0]?.value

    if (status?.update?.success && status?.update?.status) {
      // force status closed after tab
      conversation.status = status?.update?.status
      if (status?.update?.status === 'closed') {
        conversation.message = []
      } else {
        conversation.message = messages[activeConversation].map((msg) => ({
          ...msg,
          unread: false,
        }))
      }

      dispatch(setConversations(conversation))

      props.setOpen(false)

      enqueueSnackbar(t('alerts.conversationClosed'), { variant: 'success' })
      if (isFunction(props?.onSubmit)) {
        let obj = {
          status: status?.update,
          conversationTA,          
        }
        props?.onSubmit(obj);        
      }
    } else if (status?.update?.error) {
      props.setOpen(false)
      enqueueSnackbar(
        t(message ? `toasts.${message}` : 'toasts.conversationNotClosed'),
        { variant: 'error' }
      )
    }
  }, [status?.update])

  useEffect(() => setExpanded(!isTabulated), [isTabulated])

  const updateStatus = (action, data) => {
    if (action && data)
      setStatus((e) => ({ ...e, [action]: { ...defaultStatus, ...data } }))
  }

  const closeConversation = async () => {
    if (activeConversation) {
      updateStatus('update', { loading: true }) 
      setConversationTA({ startConversation: null, endConversation: null });     
      try {
        const data = { status: 'closed' }

        if (tabInfo?.id) data.tabId = tabInfo?.id

        const response = await messagesApi.post(
          `/tech4h-messages/conversation/change-status?id=${activeConversation}`,
          data
        )

        if (response.status === 200) {
          const isValid = cockpitSkills.includes(conversation?.skillId)

          if (isValid) {
            const body = [
              {
                conversationId: activeConversation,
                nomeSolicitante: conversation?.consumer?.name,
                arriving: false,
                matricula: user?.extra_matricula,
              },
            ]

            await cockpit(body)
          }
          let message = response?.data?.message
          let { hasPosChat, conversationTA:conversationTAResponse } = response?.data?.data || {}  

          if(conversationTAResponse){
            setConversationTA(conversationTAResponse);
          }
          const status = hasPosChat ? 'pos-chat' : 'closed'
          setUpdateDemand(true)
          updateStatus('update', { success: true, message: message, status })
          dispatch(updateMetrics())
        } else {
          throw response.data
        }
      } catch (error) {
        let message =
          error?.response?.status === 500
            ? t('alerts.unavailableService')
            : error?.response?.data?.message
        updateStatus('update', { error: true, message: message })
      }
    }
  }  

  const registerDemand = async () => {
    const chatId = activeConversation
    const tabId = tabInfo?.id
    setUpdateDemandLoading(true)
    if (chatId && tabId) {
      try {
        dispatch(getTab())
        const response = await messagesApi.put(
          `/tech4h-messages/conversation/tab/${chatId}`,
          { tab_id: tabId }
        )
        if (response.status === 200) {
          setUpdateDemand(true)
        } else {
          throw response.data
        }
      } catch (error) {
        const message = error?.response?.data?.message ?? 'Unavailable service'
        dispatch(tabsError({ action: 'get_tab', message: message }))
      }
    }
    setUpdateDemand(false)
    props.setOpen(false)
    setUpdateDemandLoading(false)
    dispatch(setActiveConversation(null))
  }

  const renderDemandClosed = () => (
    <div className={classes.root}>
      <div className={classes.title}>
        <Typography variant="body1">{t('alerts.closeChatQuestion')}</Typography>
      </div>
      <div
        className={classes.menuBar}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Typography variant="body1">{t('commons.registerDemand')}</Typography>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          {expanded ? (
            <UpIcon
              style={{ marginLeft: 10, color: theme.palette.primary.main }}
            />
          ) : (
            <DownIcon
              style={{ marginLeft: 10, color: theme.palette.primary.main }}
            />
          )}
        </div>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {tabs?.status?.get_tabs?.loading && (
          <div className={classes.loadingSection}>
            <CircularProgress size={25} color="primary" />
          </div>
        )}
        <Demand
          chatId={activeConversation}
          hideButton={true}
          tabInfo={setTabInfo}
          isTabulated={(status) => setIsTabulated(status)}
          update={updateDemand}
          isRefDocument
        />
      </Collapse>
    </div>
  )

  const renderDemand = () => (
    <div className={classes.root}>
      <Demand
        chatId={activeConversation}
        hideButton={true}
        tabInfo={setTabInfo}
        isTabulated={(status) => setIsTabulated(status)}
        update={updateDemand}
        updateDemandLoading={updateDemandLoading}
        isRefDocument
      />
    </div>
  )

  const disableButton = useMemo(() => {
    if (
      Object.keys(tabInfo).length === 0 &&
      objFeatureTab?.required === false
    ) {
      return false
    }

    if (objFeatureTab?.active && !tabInfo?.active) {
      return true
    }

    return false
  }, [objFeatureTab, tabInfo])

  const renderContent = () => {
    return (
      <>
        {
          <Alert
            title={
              props.open?.tabulation
                ? t('commons.registerDemand')
                : t('commons.closeService')
            }
            open={props?.setOpen}
            loading={status?.update?.loading}
            disableBackdropClick={status?.update?.loading}
            disableEscapeKeyDown={status?.update?.loading}
            agreeText={
              objFeatureTab?.active
                ? props?.open?.tabulation
                  ? t('buttons.tabConversation')
                  : t('commons.saveAndClose')
                : t('commons.close')
            }
            disagreeText={t('buttons.back')}
            disabledButton={disableButton || updateDemandLoading}
            agreeAction={() => {
              props?.open?.tabulation ? registerDemand() : closeConversation()
            }}
          >
            {objFeatureTab?.active ? (
              <>
                {props?.open?.tabulation
                  ? renderDemand()
                  : renderDemandClosed()}
              </>
            ) : (
              <div className={classes.message}>
                {t('alerts.doYouWantToCloseChat')}
              </div>
            )}
          </Alert>
        }
      </>
    )
  }

  return renderContent()
}

export default memo(ChatClose)
